import React, { useMemo, useState } from "react";
import { Input, Select, Tabs, Radio, Empty, message } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FLOW_CATEGORY_OPTIONS, FLOW_TEMPLATE_OPTIONS } from "../constants";
import WhatsAppFlowPreview from "../preview/WhatsAppFlowPreview";
import { createFlow } from "../../../../api/Api";

const NAME_CHAR_LIMIT = 200;

const TEMPLATE_TABS = [
  { key: "without-endpoint", label: "Choose Template", disabled: false },
  // { key: "with-endpoint", label: "With Endpoint", disabled: true },
];

const CreateFlow = () => {
  const [name, setName] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [activeTemplateTab, setActiveTemplateTab] =
    useState("without-endpoint");
  const [selectedTemplate, setSelectedTemplate] = useState(
    FLOW_TEMPLATE_OPTIONS.WITHOUT_ENDPOINT?.[0]?.value
  );
  const [selectedTemplateData, setSelectedTemplateData] = useState(
    FLOW_TEMPLATE_OPTIONS.WITHOUT_ENDPOINT?.[0]?.template
  );
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();
  const { currentBrand } = useSelector((state) => state.app);

  const categoryOptions = useMemo(
    () =>
      (FLOW_CATEGORY_OPTIONS || []).map((c) => ({
        label: c.name,
        value: c.code,
      })),
    []
  );

  const withoutEndpointTemplates = FLOW_TEMPLATE_OPTIONS.WITHOUT_ENDPOINT || [];

  const handleNameChange = (e) => {
    const v = e.target.value?.slice(0, NAME_CHAR_LIMIT) ?? "";
    setName(v);
  };

  const handleTemplateChange = (templateValue) => {
    setSelectedTemplate(templateValue);
    // Find the selected template and update the template data
    const selected = withoutEndpointTemplates.find(
      (t) => t.value === templateValue
    );
    if (selected) {
      setSelectedTemplateData(selected.template);
    }
  };

  const isCreateDisabled =
    !name.trim() ||
    !selectedTemplateData ||
    selectedCategories.length === 0 ||
    !currentBrand?.brand_id;

  const handleCreateFlow = async () => {
    if (isCreating || isCreateDisabled) return;

    const payload = {
      name: name.trim(),
      categories: selectedCategories,
      flow_json:
        typeof selectedTemplateData === "string"
          ? selectedTemplateData
          : JSON.stringify(selectedTemplateData),
      brand_id: currentBrand?.brand_id,
    };

    try {
      setIsCreating(true);
      const response = await createFlow(payload);
      const createdFlow =
        response?.data?.flow ||
        response?.data?.data ||
        response?.data ||
        response;
      const createdFlowId =
        createdFlow?.id ||
        createdFlow?.flow_id ||
        createdFlow?.data?.id ||
        createdFlow?.data?.flow_id;

      message.success("Flow created successfully");

      if (createdFlowId) {
        navigate(`/user/channels/whatsapp/flows/${createdFlowId}/edit`);
      } else {
        navigate("/user/channels/whatsapp/flows");
      }
    } catch (error) {
      console.error("Failed to create flow:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create flow. Please try again.";
      message.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDiscard = () => {
    if (!isCreating) {
      navigate("/user/channels/whatsapp/flows");
    }
  };

  return (
    <div className="h-full w-full bg-[#F5F7FA] p-6">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        {/* Left: Config */}
        <div className="flex flex-col gap-6">
          {/* Name */}
          <section className="rounded-xl p-[1px] bg-gradient-to-b from-[#e8ecf7] to-[#dfe9f3]">
            <div className="w-full rounded-xl bg-white p-[16px]">
              <label
                className="block text-sm font-semibold text-gray-800"
                htmlFor="flow-name"
              >
                Name <span className="text-red-500">*</span>
              </label>
              <div className="mt-2 flex items-center rounded-md  px-3 py-2 !border border-gray-200">
                <Input
                  id="flow-name"
                  bordered={false}
                  placeholder="Enter name"
                  value={name}
                  onChange={handleNameChange}
                  className="!border-none !p-0"
                  maxLength={NAME_CHAR_LIMIT}
                />
                <span className="ml-2 text-xs text-gray-400">
                  {name.length}/{NAME_CHAR_LIMIT}
                </span>
              </div>
            </div>
          </section>

          {/* Category */}
          <section className="rounded-xl p-[1px] bg-gradient-to-b from-[#e8ecf7] to-[#dfe9f3]">
            <div className="w-full rounded-xl bg-white p-[16px]">
              <h2 className="text-sm font-semibold text-gray-800">
                Category <span className="text-red-500">*</span>
              </h2>
              <div className="mt-3">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Select category"
                  className="w-full"
                  options={categoryOptions}
                  value={selectedCategories}
                  onChange={setSelectedCategories}
                  maxTagCount="responsive"
                  listHeight={256}
                />
              </div>
            </div>
          </section>

          {/* Template */}
          <section className="rounded-xl p-[1px] bg-gradient-to-b from-[#e8ecf7] to-[#dfe9f3]">
            <div className="w-full rounded-xl bg-white p-[16px]">
              {/* <h2 className="text-sm font-semibold text-gray-800 mb-3">
                Template <span className="text-red-500">*</span>
              </h2> */}

              <Tabs
                className=""
                items={TEMPLATE_TABS.map((tab) => ({
                  ...tab,
                  children:
                    tab.key === "without-endpoint" ? (
                      <Radio.Group
                        className="flex flex-col gap-3"
                        onChange={(e) => handleTemplateChange(e.target.value)}
                        value={selectedTemplate}
                      >
                        {withoutEndpointTemplates.map((t) => {
                          const active = selectedTemplate === t.value;
                          return (
                            <button
                              key={t.value}
                              type="button"
                              onClick={() => handleTemplateChange(t.value)}
                              className={[
                                "text-left w-full rounded-md border px-4 py-3 transition",
                                active
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 hover:border-blue-400 hover:bg-gray-50",
                              ].join(" ")}
                            >
                              <div className="flex items-center gap-3">
                                <Radio value={t.value} checked={active} />
                                <span className="text-[14px] text-gray-800">
                                  {t.label}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </Radio.Group>
                    ) : (
                      <div className="flex min-h-[160px] items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50">
                        <Empty
                          description="Endpoint templates coming soon"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      </div>
                    ),
                }))}
                activeKey={activeTemplateTab}
                onChange={(key) => {
                  if (key === "with-endpoint") return; // keep disabled UX
                  setActiveTemplateTab(key);
                }}
                tabBarGutter={24}
              />
            </div>
          </section>

        </div>

        <div className="flex flex-col gap-4">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="min-w-[120px] rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-50"
              onClick={handleDiscard}
              disabled={isCreating}
            >
              Discard
            </button>
            <button
              type="button"
              className={`min-w-[140px] rounded-md !bg-[var(--new-primary)] px-4 py-2 text-sm font-medium !text-white transition-colors ${
                isCreateDisabled || isCreating
                  ? "cursor-not-allowed opacity-70"
                  : "!hover:bg-[var(--new-primary-dark)]"
              }`}
              onClick={handleCreateFlow}
              disabled={isCreateDisabled || isCreating}
            >
              {isCreating ? "Creating..." : "Create Flow"}
            </button>
          </div>

          <WhatsAppFlowPreview
            flow_json={selectedTemplateData}
            selectedTemplate={selectedTemplate}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateFlow;
